import axios from 'axios';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'https://go.sentra.be',
  withCredentials: true
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Extracts a user-friendly error message from an API error response.
 * Prioritizes the 'detail' field from the server response.
 * @param error - The error object from axios
 * @param fallbackMessage - Optional fallback message if no detail is found
 * @returns A user-friendly error message string
 */
export function getErrorMessage(error: any, fallbackMessage?: string): string {
  // Check if there's a response with data
  if (error.response?.data) {
    const data = error.response.data;
    
    // Check for 'detail' field first (primary error message from Django)
    if (data.detail) {
      const detail = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
      // Only show detail if it's reasonable length (not debug output)
      if (detail.length < 500) {
        return detail;
      }
    }
    
    // Check for 'error' field
    if (data.error) {
      const error = typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
      if (error.length < 500) {
        return error;
      }
    }
    
    // Check for 'message' field
    if (data.message) {
      const message = typeof data.message === 'string' ? data.message : JSON.stringify(data.message);
      if (message.length < 500) {
        return message;
      }
    }
    
    // Check for validation errors (common in Django REST Framework)
    if (typeof data === 'object' && !Array.isArray(data)) {
      const firstKey = Object.keys(data)[0];
      if (firstKey && data[firstKey]) {
        const fieldError = Array.isArray(data[firstKey]) ? data[firstKey][0] : data[firstKey];
        if (typeof fieldError === 'string' && fieldError.length < 500) {
          return `${firstKey}: ${fieldError}`;
        }
      }
    }
  }
  
  // Use fallback message or a generic one
  return fallbackMessage || 'An error occurred. Please try again.';
}

/**
 * Shows a toast notification with the error message extracted from the API error.
 * @param error - The error object from axios
 * @param title - The title for the toast notification
 * @param fallbackMessage - Optional fallback message if no detail is found
 */
export function showErrorToast(error: any, title: string = 'Error', fallbackMessage?: string) {
  const description = getErrorMessage(error, fallbackMessage);
  
  toast({
    title,
    description,
    variant: 'destructive',
  });
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Only logout on 401 if we actually got a response from the server
    // Network errors or server down scenarios won't have error.response
    if (error.response?.status === 401) {
      const auth = useAuth.getState();
      await auth.logout();
    }
    // Don't logout on network errors - let ServerHealthCheck handle it
    return Promise.reject(error);
  }
);

export default api;